/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/create-edit/* (non-ecommerce, reason in screen-map §8),
 * with the Part-B/cancel history composed as a WidgetCard wrapping the vendor's own
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/timeline.tsx Timeline — the
 * same component apps/admin/src/pages/invoicing/Registration.tsx already uses for its attempt
 * log. Unlike Audit.tsx's audit trail (a raw table, by that screen's own disclosed exception),
 * this really is a shipment log — vehicle changes and cancellations against one e-way bill — so
 * tracking-history.tsx's own Collapse+Timeline pattern applies here directly.
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useLoaderData, useOutletContext, useParams, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { CaveatBanner, PageHeader, ErrorSurface, Field, EmptyState, WidgetCard } from "@steelyard/theme";
import { EWayBillState, type EWayBillStateName } from "@steelyard/theme";
import { ReasonDialog } from "@steelyard/theme";
import { TransportDetailsForm, type TransportField } from "@steelyard/theme";
import Timeline from "@core/app/shared/logistics/tracking/timeline";
import { can } from "../../lib/permissions";
import { formatDate, formatTime } from "../../lib/format";
import {
  cancelEWayBill,
  generateEWayBill,
  getDispatchData,
  NOTHING_SUBMITTED,
  TRANSPORT_MODES,
  updatePartB,
  type DispatchData,
  type DispatchPincodes,
  type DispatchState,
} from "../../lib/services/dispatch";
import { isCancelled } from "../../lib/services/invoicing";
import { dispatchPath, invoicePath, orderPath } from "../../lib/paths";
import type { AccountSummary, PermissionState } from "../../types";
import { Text, Button } from "rizzui";

/**
 * Ported from apps/admin/app/(app)/dispatch/{[invoiceId]/page,[invoiceId]/capture}.tsx. The
 * capture form doesn't need a separate "client component" file the way the Next version did —
 * every component here is already client-rendered, so DispatchCapture is inlined below.
 */
const EWAY_STATE: Record<string, EWayBillStateName> = {
  not_applicable: "not_applicable",
  pending: "pending",
  generated: "active",
  part_b_updated: "active",
  failed: "failed",
  expired: "expired",
  cancelled: "cancelled",
};

/** The RFC 9457 detail first — `ApiError.message` is only the title. */
function problemText(err: unknown): string | undefined {
  const problem = (err ?? {}) as { detail?: string; title?: string; message?: string };
  return problem.detail ?? problem.title ?? problem.message;
}

export async function dispatchLoader({ params, request }: LoaderFunctionArgs) {
  return getDispatchData(request, params.invoiceNumber!);
}

export default function DispatchPage() {
  const data = useLoaderData() as DispatchData;
  // The URL's own key: the number, or the id where the number is ambiguous (that id URL stays).
  const { invoiceNumber: key = "" } = useParams<{ invoiceNumber: string }>();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const [partBError, setPartBError] = useState<string | undefined>();
  const [cancelError, setCancelError] = useState<string | undefined>();

  if (!can(permissionState, "dispatch.assign")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="record this dispatch" />;
  }

  if (data.invoiceNotFound) {
    return (
      <div className="mx-auto w-full max-w-md">
        <PageHeader title="Dispatch" breadcrumb={[{ name: "Home", href: "/" }, { name: "Dispatch" }]} />
        <EmptyState title="There is no invoice to dispatch against" action={<Link to="/invoicing"><Button as="span" size="lg" variant="outline">Find the invoice</Button></Link>}>
          An e-way bill is generated against a raised invoice, and nothing is raised for this reference yet. The order has to be invoiced before its transport details can be captured.
        </EmptyState>
      </div>
    );
  }
  if (!data.invoice || !data.status) {
    return <ErrorSurface error={{ title: "The invoice or its e-way bill status is missing." }} action="load this dispatch" />;
  }

  const invoiceId = data.invoice.id;
  const href = dispatchPath(key);
  const confirm = searchParams.get("confirm");
  const word = data.status.ewaybillStatus;
  const state = EWAY_STATE[word] ?? "pending";
  const number = data.status.ewaybillNo ?? undefined;
  const invoiceCancelled = isCancelled(data.invoice.state);
  // A third-party transporter's order: no transport details are asked for (owner decision, 2026-10-26).
  const thirdParty = data.deliveryType === "third_party";
  // The e-way bill is only the paperwork. The order reaches Dispatched/Delivered through the
  // dispatcher workflow, not the state menu (the advance endpoint 409s `dispatch_workflow_required`),
  // so this only points at the order. Offered once the consignment may move: no bill needed, or an
  // active one exists.
  const mayMove = !invoiceCancelled && (state === "not_applicable" || state === "active" || thirdParty) && can(permissionState, "orders.advance");
  const orderHref = data.invoice.orderId ? orderPath(data.invoice.orderNumber || data.invoice.orderId) : "/orders?state=invoiced";
  const markDispatched = mayMove ? (
    <EmptyState
      title="The consignment can move"
      action={
        <Link to={orderHref}>
          <Button as="span" size="lg">{data.invoice.orderId ? "Open the order" : "Find the invoiced order"}</Button>
        </Link>
      }
    >
      {thirdParty
        ? "This order goes with a third-party transporter, so no transport details are asked for. "
        : state === "not_applicable"
          ? "No e-way bill is needed for this consignment. "
          : "The e-way bill is in place. "}
      The order for invoice {data.invoice.invoiceNumber} is dispatched and delivered through the dispatch workflow, not
      from this screen — open it to see where it stands.
    </EmptyState>
  ) : null;

  function closeDialog() {
    setPartBError(undefined);
    setCancelError(undefined);
    setSearchParams((p) => {
      p.delete("confirm");
      return p;
    });
  }

  async function handlePartB(formData: FormData) {
    const vehicle = String(formData.get("newVehicleNumber") ?? "").trim();
    const reason = String(formData.get("reason") ?? "").trim();
    if (vehicle === "") return setPartBError("Enter the new vehicle number.");
    if (reason === "") return setPartBError("A reason is required.");
    try {
      await updatePartB(invoiceId, vehicle, reason);
      closeDialog();
      toast.success("Part-B updated");
      revalidator.revalidate();
    } catch (err) {
      const message = problemText(err) ?? "The service refused this update.";
      setPartBError(message);
      toast.error(message);
    }
  }

  async function handleCancel(formData: FormData) {
    const reason = String(formData.get("reason") ?? "").trim();
    if (reason === "") return setCancelError("A reason is required.");
    try {
      await cancelEWayBill(invoiceId, reason);
      closeDialog();
      toast.success("E-way bill cancelled");
      revalidator.revalidate();
    } catch (err) {
      const message = problemText(err) ?? "The service refused this cancellation.";
      setCancelError(message);
      toast.error(message);
    }
  }

  const body = (
    <div className="flex flex-col gap-6">
      {number ? (
        <>
          <EWayBillState state={state} number={number} validUntil={<>Reported by the service as “{word}”</>} vehicle={<History rows={data.audit} error={data.auditError} />}>
            {/* A cancelled or expired bill keeps its number but can no longer be changed or cancelled. */}
            {state === "active" ? (
              <div className="flex flex-wrap gap-2">
                <Link to={`${href}?confirm=part-b`}><Button as="span" size="lg" variant="outline">Vehicle changed</Button></Link>
                <Link to={`${href}?confirm=cancel`}><Button as="span" size="lg" variant="outline">Cancel this bill</Button></Link>
              </div>
            ) : null}
          </EWayBillState>
          {markDispatched}

          {/* Only the open dialog is mounted — ReasonDialog's textarea id is fixed. */}
          {confirm === "part-b" ? (
          <ReasonDialog
            title="Record the new vehicle"
            consequence={<>Part-B is updated at the portal in place: bill {number} keeps its number, and the vehicle it previously carried stays in the history below rather than being overwritten.</>}
            action={handlePartB}
            confirmLabel="Update Part-B"
            reasonLabel="Why the vehicle changed"
            error={partBError}
            cancel={<Button type="button" variant="outline" size="lg" onClick={closeDialog}>Leave it</Button>}
          >
            <Field label="New vehicle number" name="newVehicleNumber" required autoCapitalize="characters" autoCorrect="off" spellCheck={false} className="h-12 text-base" />
          </ReasonDialog>
          ) : null}

          {confirm === "cancel" ? (
          <ReasonDialog
            title="Cancel this e-way bill"
            consequence={<>The bill is cancelled at the portal. The consignment may not move on it afterwards, and the number is retained on the invoice rather than removed.</>}
            action={handleCancel}
            confirmLabel="Cancel the e-way bill"
            error={cancelError}
            cancel={<Button type="button" variant="outline" size="lg" onClick={closeDialog}>Keep it</Button>}
          />
          ) : null}
        </>
      ) : (
        <>
          {/* not_applicable: no e-way bill is needed for this consignment, so nothing to capture. */}
          <EWayBillState state={state} />
          {state !== "not_applicable" && !thirdParty ? <DispatchCapture invoiceId={invoiceId} pincodes={data.pincodes ?? null} /> : null}
          {markDispatched}
        </>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-md">
      <div className="flex flex-col gap-6">
        <>
          <PageHeader
            title={`Invoice ${data.invoice.invoiceNumber}`}
            breadcrumb={[{ name: "Home", href: "/" }, { name: "Dispatch" }, { name: String(`Invoice ${data.invoice.invoiceNumber}`) }]}
          ></PageHeader>
          <Text className="mb-6 max-w-2xl text-gray-500">{<>Dated {formatDate(data.invoice.invoiceDate)} · {data.invoice.recipientLegalName}</>}</Text>
        </>

        {invoiceCancelled ? (
          <CaveatBanner
            caveat={number ? "This invoice is cancelled and still carries an e-way bill." : "This invoice is cancelled; the details below will not apply."}
            action={<Link to={invoicePath(key)} className="underline">Open the invoice</Link>}
          >
            {body}
          </CaveatBanner>
        ) : (
          body
        )}
      </div>
    </div>
  );
}

const REQUIRED = "Needed before an e-way bill can be generated.";

/** Where Billing takes each pincode from, or why it is not shown. Neither is typed (owner decision, 2026-10-26). */
function pincodeHints(pincodes: DispatchPincodes | null) {
  if (!pincodes) {
    const unread = "Could not be read just now. Billing still sets it when the details are saved.";
    return { fromPinCode: unread, toPinCode: unread };
  }
  return {
    fromPinCode: pincodes.fromPincode
      ? "Your registered place of business. Set by Billing."
      : "Not set: add the PIN code to the place of business in Integration settings first.",
    toPinCode: pincodes.toPincode
      ? "The order's delivery address, the one its delivery charge was priced on."
      : "The order has no delivery pincode.",
  };
}

/** Adapted from Next's useActionState to plain useState — no server-action boundary to work around here. */
function DispatchCapture({ invoiceId, pincodes }: { invoiceId: string; pincodes: DispatchPincodes | null }) {
  const [state, setState] = useState<DispatchState>(NOTHING_SUBMITTED);
  const [pending, setPending] = useState(false);
  const revalidator = useRevalidator();

  async function handleSubmit(formData: FormData) {
    setPending(true);
    try {
      const next = await generateEWayBill(invoiceId, formData);
      setState(next);
      if (next.generated) {
        toast.success("E-way bill generated");
        revalidator.revalidate();
      } else if (next.error) toast.error("Could not generate the e-way bill");
    } finally {
      setPending(false);
    }
  }

  const errors = Object.fromEntries(state.missing.map((field) => [field, REQUIRED])) as Partial<Record<TransportField, string>>;

  return (
    <TransportDetailsForm
      action={handleSubmit}
      modes={TRANSPORT_MODES}
      values={{ ...state.values, fromPinCode: pincodes?.fromPincode ?? "", toPinCode: pincodes?.toPincode ?? "" }}
      locked={pincodeHints(pincodes)}
      errors={errors}
      submitLabel={state.persisted && state.error ? "Generate again — details are saved" : "Generate e-way bill"}
    >
      {pending ? <p role="status" className="text-muted-foreground text-sm">Saving the details, then asking for the bill…</p> : null}

      {state.error ? (
        <ErrorSurface error={state.error} action={state.persisted ? "generate the e-way bill" : "save the transport details"}>
          {state.persisted ? <p className="text-sm font-medium">The details below are saved against this invoice. Nothing needs re-typing — press the button again.</p> : null}
        </ErrorSurface>
      ) : null}

      {state.generated ? (
        <p role="status" className="text-green-dark text-sm font-medium">
          E-way bill {state.generated.number ?? "generated"} · {state.generated.status}
        </p>
      ) : null}
    </TransportDetailsForm>
  );
}

function History({ rows, error }: { rows: DispatchData["audit"]; error: unknown }) {
  if (error) return <ErrorSurface error={error} action="load this bill's history" />;

  return (
    <WidgetCard title={<>History — nothing is overwritten</>} className="overflow-hidden" headerClassName="border-muted border-b">
      {rows.length === 0 ? (
        <div className="pt-4">
          <EmptyState title="No entries yet">Nothing is recorded against this bill yet.</EmptyState>
        </div>
      ) : (
        <Timeline
          className="pt-4"
          order="desc"
          data={[...rows].reverse().map((row) => ({
            title: row.action,
            text: row.reason ?? "None given",
            hightlightedText: "",
            date: formatDate(row.occurredAt),
            time: formatTime(row.occurredAt) ?? "",
            status: "",
          }))}
        />
      )}
    </WidgetCard>
  );
}
